import { useCallback, useMemo } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { AnalyticsFilters } from "@/lib/api";

/** The quick date presets, in the order the filter bar shows them. */
export const DATE_PRESETS = ["today", "last7", "last30", "thisMonth", "allTime"] as const;

export type DatePreset = (typeof DATE_PRESETS)[number];

export const DATE_PRESET_LABELS: Record<DatePreset, string> = {
  today: "Today",
  last7: "Last 7 days",
  last30: "Last 30 days",
  thisMonth: "This month",
  allTime: "All time",
};

/** The array-valued filter keys, i.e. every multi-select dimension. */
export const MULTI_FILTER_KEYS = [
  "courseIds",
  "categories",
  "batchIds",
  "groupIds",
  "teamLeaderIds",
  "trainingStatuses",
  "enrollmentStatuses",
] as const;

export type MultiFilterKey = (typeof MULTI_FILTER_KEYS)[number];

/**
 * The URL shape of the filter state: one comma-joined string per multi-select dimension.
 *
 * Comma-joined rather than repeated params because this is the ROUTER's representation, and the
 * router's search serialiser does not round-trip repeated keys into arrays. The API layer is where
 * the arrays become repeated params (see `buildQueryString`), so the two concerns stay separate and
 * each uses the encoding its own consumer binds from.
 */
export interface AnalyticsSearch {
  from?: string;
  to?: string;
  studentId?: string;
  courseIds?: string;
  categories?: string;
  batchIds?: string;
  groupIds?: string;
  teamLeaderIds?: string;
  trainingStatuses?: string;
  enrollmentStatuses?: string;
}

const asString = (value: unknown): string | undefined =>
  typeof value === "string" && value.length > 0 ? value : undefined;

/**
 * The route's search whitelist. TanStack Router drops any param a route does not declare here, so
 * every filter key must be listed or it silently vanishes on the next navigation.
 */
export function validateAnalyticsSearch(search: Record<string, unknown>): AnalyticsSearch {
  const validated: AnalyticsSearch = {
    from: asString(search.from),
    to: asString(search.to),
    studentId: asString(search.studentId),
  };

  for (const key of MULTI_FILTER_KEYS) validated[key] = asString(search[key]);

  return validated;
}

/** `yyyy-MM-dd` in local time — the format both `<input type="date">` and the API expect. */
function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * The `from`/`to` a preset resolves to. "All time" is the absence of both — the backend reads that as
 * unbounded and falls back to a trailing window for the trend axis only.
 */
export function resolvePreset(
  preset: DatePreset,
  today = new Date(),
): { from?: string; to?: string } {
  const to = toIsoDate(today);

  switch (preset) {
    case "today":
      return { from: to, to };
    case "last7": {
      const from = new Date(today);
      from.setDate(from.getDate() - 6);
      return { from: toIsoDate(from), to };
    }
    case "last30": {
      const from = new Date(today);
      from.setDate(from.getDate() - 29);
      return { from: toIsoDate(from), to };
    }
    case "thisMonth": {
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: toIsoDate(from), to };
    }
    case "allTime":
      return {};
  }
}

/**
 * Which preset the current range corresponds to, or null for a custom range.
 *
 * Derived rather than stored: a stored preset and a stored range are two sources of truth for one
 * fact, and they drift the moment the user edits a date by hand or reloads on a later day.
 */
export function activePreset(filters: AnalyticsFilters, today = new Date()): DatePreset | null {
  for (const preset of DATE_PRESETS) {
    const range = resolvePreset(preset, today);
    if (range.from === filters.from && range.to === filters.to) return preset;
  }
  return null;
}

const splitList = (value: string | undefined): string[] | undefined => {
  const items = (value ?? "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
  return items.length > 0 ? items : undefined;
};

const joinList = (values: string[] | undefined): string | undefined =>
  values && values.length > 0 ? values.join(",") : undefined;

export function searchToFilters(search: AnalyticsSearch): AnalyticsFilters {
  const filters: AnalyticsFilters = {
    from: search.from,
    to: search.to,
    studentId: search.studentId,
  };

  for (const key of MULTI_FILTER_KEYS) filters[key] = splitList(search[key]);

  return filters;
}

export function filtersToSearch(filters: AnalyticsFilters): AnalyticsSearch {
  const search: AnalyticsSearch = {
    from: filters.from,
    to: filters.to,
    studentId: filters.studentId,
  };

  for (const key of MULTI_FILTER_KEYS) search[key] = joinList(filters[key]);

  return search;
}

/**
 * Owns the shared filter state for the Analytics page, persisted in the URL.
 *
 * <p>A single filter bar mutates this state and every per-tab query hook takes the resulting object,
 * so changing a filter produces a new queryKey and refetches with no manual cache invalidation.</p>
 *
 * <p>The URL is the store rather than component state: filters previously lived in a `useState` in
 * the page component, so a reload, a shared link, or navigating away and back silently reset every
 * selection while the tabs went on showing figures for a range the user could no longer see.</p>
 */
export function useAnalyticsFilters() {
  const navigate = useNavigate();
  const search = (useSearch({ strict: false }) ?? {}) as AnalyticsSearch;

  const filters = useMemo(() => searchToFilters(search), [search]);

  const commit = useCallback(
    (next: AnalyticsFilters) => {
      // `replace` so adjusting a filter does not stack a history entry per change — Back should
      // leave the analytics page, not walk back through every intermediate filter state.
      void navigate({ to: ".", search: filtersToSearch(next), replace: true });
    },
    [navigate],
  );

  const setFilter = useCallback(
    (patch: Partial<AnalyticsFilters>) => commit({ ...filters, ...patch }),
    [commit, filters],
  );

  /** Adds or removes one value from a multi-select dimension. */
  const toggleValue = useCallback(
    (key: MultiFilterKey, value: string) => {
      const current = filters[key] ?? [];
      const next = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      commit({ ...filters, [key]: next.length > 0 ? next : undefined });
    },
    [commit, filters],
  );

  const clearDimension = useCallback(
    (key: MultiFilterKey) => commit({ ...filters, [key]: undefined }),
    [commit, filters],
  );

  const applyPreset = useCallback(
    (preset: DatePreset) => {
      const range = resolvePreset(preset);
      commit({ ...filters, from: range.from, to: range.to });
    },
    [commit, filters],
  );

  const clearFilters = useCallback(() => commit({}), [commit]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.from || filters.to) count += 1;
    if (filters.studentId) count += 1;
    for (const key of MULTI_FILTER_KEYS) count += filters[key]?.length ?? 0;
    return count;
  }, [filters]);

  return {
    filters,
    setFilter,
    toggleValue,
    clearDimension,
    applyPreset,
    clearFilters,
    activeFilterCount,
    hasActiveFilters: activeFilterCount > 0,
    preset: activePreset(filters),
  };
}
